export type ElementType = 'light' | 'camera' | 'character';

export interface CanvasElement {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  scale: number;
  label: string;
  properties: Record<string, string>;
}

export type AspectRatio = '1:1' | '4:5' | '5:4' | '3:4' | '4:3' | '2:3' | '3:2' | '9:16' | '16:9' | '21:9' | '2.39:1' | '1.85:1' | '5:7' | '7:5' | '1:2' | '2:1';

export type PromptFormat = 'structured' | 'weighted' | 'natural' | 'json' | 'markdown' | 'tags' | 'yaml' | 'graph' | 'bullet' | 'dsl';

export type AppMode = 'basic' | 'pro' | 'advanced';

export interface GlobalSettings {
  // Quality & Rendering
  renderStyle: string;
  textureDetail: number;
  skinDetail: number;
  filmGrain: boolean;
  hdrControl: number;
  resolutionScale: string;
  dynamicRange: string;
  realCameraMode: boolean;

  // Perspective & Composition
  ruleOfThirds: boolean;
  goldenRatio: boolean;
  symmetryMode: boolean;
  centeredCinematic: boolean;
  negativeSpace: number;
  horizonTilt: number;
  cameraHeight: number;
  framingPreset: string;
  subjectCentering: number;

  // VFX
  particles: string;
  atmosphericDensity: number;
  motionTrails: boolean;
  energyAura: boolean;
  lightStreaks: boolean;
  chromaticAberration: number;
  glitchEffect: boolean;
  explosionIntensity: number;
  cinematicSmoke: number;
  fireFlame: number;
  lightningOverlay: boolean;
  fogHaze: number;
  dustParticles: number;
  godRaysGlobal: number;
  bloomGlow: number;
  motionBlurGlobal: number;

  // Color Grading
  lutPreset: string;
  filmStock: string;
  contrastCurve: number;
  highlightBalance: number;
  shadowBalance: number;
  saturationIntensity: number;
  splitToningHighlight: string;
  splitToningShadow: string;
  tealOrangePreset: boolean;
  vintageMode: boolean;

  // Texture & Detail
  microDetail: number;
  sharpness: number;
  surfaceClarity: number;
  fabricTexture: number;
  highFrequency: number;

  // Skin & Beauty
  skinSmoothing: number;
  texturePreservation: number;
  poreRefinement: number;
  blemishRemoval: number;
  underEyeCorrection: number;
  skinToneBalance: number;
  highlightGlow: number;
  freqSeparation: boolean;
  beautyPreset: string;
  naturalFashionToggle: string;

  // Rendering & Output
  renderQuality: string;
  noiseReduction: number;
  upscaleMode: string;
  exportProfile: string;
}

export const DEFAULT_GLOBAL_SETTINGS: GlobalSettings = {
  renderStyle: 'photorealistic',
  textureDetail: 70,
  skinDetail: 50,
  filmGrain: false,
  hdrControl: 50,
  resolutionScale: '4k',
  dynamicRange: 'high',
  realCameraMode: false,
  ruleOfThirds: true,
  goldenRatio: false,
  symmetryMode: false,
  centeredCinematic: false,
  negativeSpace: 30,
  horizonTilt: 0,
  cameraHeight: 50,
  framingPreset: 'none',
  subjectCentering: 50,
  particles: 'none',
  atmosphericDensity: 0,
  motionTrails: false,
  energyAura: false,
  lightStreaks: false,
  chromaticAberration: 0,
  glitchEffect: false,
  explosionIntensity: 0,
  cinematicSmoke: 0,
  fireFlame: 0,
  lightningOverlay: false,
  fogHaze: 0,
  dustParticles: 0,
  godRaysGlobal: 0,
  bloomGlow: 0,
  motionBlurGlobal: 0,
  lutPreset: 'none',
  filmStock: 'none',
  contrastCurve: 50,
  highlightBalance: 50,
  shadowBalance: 50,
  saturationIntensity: 50,
  splitToningHighlight: 'none',
  splitToningShadow: 'none',
  tealOrangePreset: false,
  vintageMode: false,
  microDetail: 50,
  sharpness: 50,
  surfaceClarity: 50,
  fabricTexture: 0,
  highFrequency: 50,
  skinSmoothing: 0,
  texturePreservation: 80,
  poreRefinement: 0,
  blemishRemoval: 0,
  underEyeCorrection: 0,
  skinToneBalance: 50,
  highlightGlow: 0,
  freqSeparation: false,
  beautyPreset: 'none',
  naturalFashionToggle: 'natural',
  renderQuality: '4k',
  noiseReduction: 20,
  upscaleMode: 'none',
  exportProfile: 'web',
};

export interface FieldConfig {
  type: 'select' | 'slider' | 'toggle' | 'text';
  key: string;
  label: string;
  tooltip?: string;
  advancedOnly?: boolean;
  proOnly?: boolean;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
}

export const ASPECT_CSS: Record<AspectRatio, string> = {
  '1:1': 'aspect-square',
  '4:5': 'aspect-[4/5]',
  '5:4': 'aspect-[5/4]',
  '3:4': 'aspect-[3/4]',
  '4:3': 'aspect-[4/3]',
  '2:3': 'aspect-[2/3]',
  '3:2': 'aspect-[3/2]',
  '9:16': 'aspect-[9/16]',
  '16:9': 'aspect-video',
  '21:9': 'aspect-[21/9]',
  '2.39:1': 'aspect-[2.39/1]',
  '1.85:1': 'aspect-[1.85/1]',
  '5:7': 'aspect-[5/7]',
  '7:5': 'aspect-[7/5]',
  '1:2': 'aspect-[1/2]',
  '2:1': 'aspect-[2/1]',
};
